import React, { useState, useMemo, useEffect, useRef } from "react";
import OrderItem from "./OrderItemProps";

export type MainTab = "ALL" | "TRANSFERRING_DIFF";
export type StatusTab = "ALL" | "PENDING" | "CONFIRMED" | "PROCESSING" | "DELIVERING" | "DELIVERED" | "COMPLETED" | "CANCELLED";

export const STATUS_TABS_CONFIG = [
    { key: "ALL", label: "Tất cả", statusId: undefined },
    { key: "PENDING", label: "Chờ xác nhận", statusId: 1 },
    { key: "CONFIRMED", label: "Đã xác nhận", statusId: 2 },
    { key: "PROCESSING", label: "Đang xử lý", statusId: 3 },
    { key: "DELIVERING", label: "Đang giao", statusId: 4 },
    { key: "DELIVERED", label: "Đã giao", statusId: 5 },
    { key: "COMPLETED", label: "Hoàn thành", statusId: 6 },
    { key: "CANCELLED", label: "Đã hủy", statusId: 0 }
] as const;

export interface OrderFilters {
    search?: string;
    startDate?: string;
    endDate?: string;
    productId?: string;
}

interface OrderPageProps {
    orders: any[];
    totalOrders: number;
    page: number;
    setPage: (page: number) => void;
    mainTab: MainTab;
    setMainTab: (tab: MainTab) => void;
    statusTab: StatusTab;
    setStatusTab: (tab: StatusTab) => void;
}

const OrderPage: React.FC<OrderPageProps> = ({ 
    orders, 
    totalOrders, 
    page, 
    setPage, 
    mainTab, 
    setMainTab, 
    statusTab, 
    setStatusTab 
}) => {
    const scrollerRef = useRef<HTMLDivElement>(null);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);
    const [indicator, setIndicator] = useState({ left: "0px", width: "0px" });

    const checkScroll = () => {
        const el = scrollerRef.current;
        if (!el) return;

        setCanScrollLeft(el.scrollLeft > 0);
        setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth);
    };

    const updateIndicator = () => {
        const activeIndex = STATUS_TABS_CONFIG.findIndex(tab => tab.key === statusTab);
        const tabWidth = 120; // Approximate width of each tab
        setIndicator({
            left: `${activeIndex * tabWidth}px`,
            width: `${tabWidth}px`
        });
    };

    useEffect(() => {
        checkScroll();
        updateIndicator();
        window.addEventListener("resize", checkScroll);
        return () => window.removeEventListener("resize", checkScroll);
    }, [statusTab]);

    const scrollTabs = (dir: "left" | "right") => {
        const el = scrollerRef.current;
        if (!el) return;

        const delta = dir === "left" ? -200 : 200;
        el.scrollBy({ left: delta, behavior: "smooth" });

        setTimeout(checkScroll, 300);
    };

    return (
        <div className="MuiGrid-root MuiGrid-container">
            {/* ================= TAB CẤP 1 ================= */}
            <div className="MuiGrid-root styles_tabs__4xrWg MuiGrid-item MuiGrid-grid-xs-12">
                <div
                    className="MuiPaper-root MuiPaper-elevation0 MuiPaper-rounded"
                    style={{ background: "white", marginTop: "15px" }}
                >
                    <div className="MuiTabs-root">
                        <div className="MuiTabs-scroller MuiTabs-fixed" style={{ overflow: "hidden" }}>
                            <div className="MuiTabs-flexContainer" role="tablist">
                                {[
                                    { key: "ALL", label: "Tất cả đơn hàng" },
                                    { key: "TRANSFERRING_DIFF", label: "Đơn chênh lệch chuyển khoản" },
                                ].map((tab) => (
                                    <button
                                        key={tab.key}
                                        className={`MuiButtonBase-root MuiTab-root styles_tab__BWO_W MuiTab-textColorInherit
                      ${mainTab === tab.key ? "Mui-selected" : ""}
                    `}
                                        type="button"
                                        role="tab"
                                        aria-selected={mainTab === tab.key}
                                        onClick={() => setMainTab(tab.key as MainTab)}
                                    >
                                        <span className="MuiTab-wrapper">{tab.label}</span>
                                        <span className="MuiTouchRipple-root"></span>
                                    </button>
                                ))}
                            </div>

                            <span
                                className="jss57 jss59 styles_indicatorOrders__UU85R"
                                style={{
                                    left: mainTab === "ALL" ? "0px" : "160px",
                                    width: "160px",
                                }}
                            ></span>
                        </div>
                    </div>
                </div>
            </div>

            {/* ================= TAB TRẠNG THÁI ================= */}
            {mainTab === "ALL" && (
                <div className="MuiTabPanel-root p-0 MuiGrid-grid-xs-12">
                    <div
                        className="MuiPaper-root MuiPaper-elevation0 MuiPaper-rounded"
                        style={{ background: "transparent", marginTop: "15px" }}
                    >
                        <div className="MuiTabs-root styles_scrollBtnDisable__9Y36B">
                            {/* NÚT SCROLL TRÁI */}
                            <div
                                className={`MuiButtonBase-root MuiTabScrollButton-root MuiTabs-scrollButtons styles_scrollBtn__imJ4U MuiTabs-scrollButtonsDesktop
      ${canScrollLeft ? "" : "Mui-disabled"}
    `}
                                aria-disabled={!canScrollLeft}
                                onClick={() => scrollTabs("left")}
                            >
                                <svg
                                    className="MuiSvgIcon-root MuiSvgIcon-fontSizeSmall"
                                    focusable="false"
                                    viewBox="0 0 24 24"
                                    aria-hidden="true"
                                >
                                    <path d="M15.41 16.09l-4.58-4.59 4.58-4.59L14 5.5l-6 6 6 6z"></path>
                                </svg>
                                <span className="MuiTouchRipple-root"></span>
                            </div>

                            {/* DIV ĐO WIDTH – BẮT BUỘC PHẢI CÓ */}
                            <div
                                className="MuiTabs-scrollable"
                                style={{
                                    width: "99px",
                                    height: "99px",
                                    position: "absolute",
                                    top: "-9999px",
                                    overflow: "scroll",
                                }}
                            ></div>

                            {/* SCROLLER */}
                            <div
                                ref={scrollerRef}
                                className="MuiTabs-scroller MuiTabs-scrollable"
                                style={{ marginBottom: "0px" }}
                            >
                                <div className="MuiTabs-flexContainer" role="tablist">
                                    {STATUS_TABS_CONFIG.map((tab) => {
                                        const active = statusTab === tab.key;
                                        return (
                                            <button
                                                key={tab.key}
                                                className={`MuiButtonBase-root MuiTab-root jss87 MuiTab-textColorPrimary
              ${active ? "Mui-selected jss88" : ""}
            `}
                                                role="tab"
                                                aria-selected={active}
                                                onClick={() => setStatusTab(tab.key as StatusTab)}
                                            >
                                                <span className="MuiTab-wrapper">
                                                    {tab.label}
                                                </span>
                                            </button>
                                        );
                                    })}
                                </div>

                                <span
                                    className="jss57 jss59 MuiTabs-indicator styles_indicator__UMOsv"
                                    style={{
                                        left: indicator.left,
                                        width: indicator.width,
                                    }}
                                ></span>
                            </div>

                            {/* NÚT SCROLL PHẢI */}
                            <div
                                className={`MuiButtonBase-root MuiTabScrollButton-root MuiTabs-scrollButtons styles_scrollBtn__imJ4U MuiTabs-scrollButtonsDesktop
      ${canScrollRight ? "" : "Mui-disabled"}
    `}
                                aria-disabled={!canScrollRight}
                                onClick={() => scrollTabs("right")}
                            >
                                <svg
                                    className="MuiSvgIcon-root MuiSvgIcon-fontSizeSmall"
                                    focusable="false"
                                    viewBox="0 0 24 24"
                                    aria-hidden="true"
                                >
                                    <path d="M8.59 16.34l4.58-4.59-4.58-4.59L10 5.75l6 6-6 6z"></path>
                                </svg>
                                <span className="MuiTouchRipple-root"></span>
                            </div>
                        </div>
                    </div>

                    {/* ================= LIST ĐƠN HÀNG ================= */}
                    <div className="MuiGrid-root MuiGrid-item MuiGrid-grid-xs-12">
                        {orders.length > 0 ? (
                            orders.map((order) => (
                                <OrderItem key={order.id} data={order} />
                            ))
                        ) : (
                             null
                        )}
                    </div>

                    {/* ================= PAGINATION ================= */}
                    {orders.length > 0 ? (
                    <div className="MuiGrid-root MuiGrid-item MuiGrid-grid-xs-12">
                        <div className="styles_justify_center__PXDc_">
                            <nav aria-label="pagination navigation" className="MuiPagination-root jss47">
                                <ul className="MuiPagination-ul border-pagination">
                                    <li>
                                        <button 
                                            className="MuiButtonBase-root MuiPaginationItem-root MuiPaginationItem-page MuiPaginationItem-outlined" 
                                            type="button" 
                                            aria-label="Go to previous page"
                                            disabled={page === 1}
                                            onClick={() => setPage(Math.max(1, page - 1))}
                                        >
                                            <svg className="MuiSvgIcon-root MuiPaginationItem-icon" focusable="false" viewBox="0 0 24 24" aria-hidden="true">
                                                <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"></path>
                                            </svg>
                                        </button>
                                    </li>
                                    <li>
                                        <button className="MuiButtonBase-root MuiPaginationItem-root MuiPaginationItem-page MuiPaginationItem-outlined Mui-selected" type="button" aria-current="true" style={{ lineHeight: '1' }}>
                                            {page}
                                            <span className="MuiTouchRipple-root"></span>
                                        </button>
                                    </li>
                                    
                                    <li>
                                        <button 
                                            className="MuiButtonBase-root MuiPaginationItem-root MuiPaginationItem-page MuiPaginationItem-outlined" 
                                            type="button" 
                                            aria-label="Go to next page"
                                            onClick={() => setPage(page + 1)}
                                        >
                                            <svg className="MuiSvgIcon-root MuiPaginationItem-icon" focusable="false" viewBox="0 0 24 24" aria-hidden="true">
                                                <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"></path>
                                            </svg>
                                        </button>
                                    </li>
                                </ul>
                            </nav>
                        </div>
                    </div>
                    ) : (
                    <div className="MuiGrid-root MuiGrid-container MuiGrid-item MuiGrid-justify-content-xs-center MuiGrid-grid-xs-12">
                        <div className="styles_not_order_container__q6iE4">
                            <div className="styles_not_order_title___1v73">Không có đơn hàng</div>
                            <div>
                                <button className="MuiButtonBase-root MuiButton-root MuiButton-text sc-2362e412-0 iQEreR sc-add9464a-0 ekbpMW border-radius-9999 p-6-12" type="button">
                                    <span className="MuiButton-label"> Đến trang đặt hàng nhanh </span>
                                    <span className="MuiTouchRipple-root"></span>
                                </button>
                            </div>
                        </div>
                    </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default OrderPage;